import emailjs from '@emailjs/browser';

// EmailJS configuration - these should be set in your .env.local file
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_techmaster';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_pilot_enrollment';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key_here';

// Initialize EmailJS only if we have a valid public key
if (EMAILJS_PUBLIC_KEY && EMAILJS_PUBLIC_KEY !== 'your_public_key_here') {
  emailjs.init(EMAILJS_PUBLIC_KEY);
}

export interface PilotEnrollmentData {
  // College Information
  collegeName: string;
  collegeType: string;
  location: string;
  establishedYear: string;
  
  // Contact Person Details
  contactPersonName: string;
  designation: string;
  department: string;
  email: string;
  phone: string;
  countryCode: string;
  
  // Program Details
  targetStudents: string;
  expectedBatchSize: string;
  preferredSemester: string;
  deliveryMode: string;
  preferredSchedule: string;
  
  // Additional Information
  currentAIPrograms: string;
  specificRequirements: string;
  budgetRange: string;
}

export const sendPilotEnrollmentEmail = async (formData: PilotEnrollmentData): Promise<boolean> => {
  try {
    // Check if EmailJS is properly configured
    if (!EMAILJS_PUBLIC_KEY || EMAILJS_PUBLIC_KEY === 'your_public_key_here') {
      console.log('EmailJS not configured, using fallback method');
      return false;
    }
    // Format the email content
    const emailParams = {
      to_email: 'globalsansys@gmail.com',
      from_name: formData.contactPersonName,
      from_email: formData.email,
      subject: `New AI Pilot Program Request - ${formData.collegeName}`,
      
      // College Information
      college_name: formData.collegeName,
      college_type: formData.collegeType,
      location: formData.location,
      established_year: formData.establishedYear,
      
      // Contact Details
      contact_person: formData.contactPersonName,
      designation: formData.designation,
      department: formData.department,
      email: formData.email,
      phone: `${formData.countryCode} ${formData.phone}`,
      
      // Program Requirements
      target_students: formData.targetStudents,
      batch_size: formData.expectedBatchSize,
      preferred_semester: formData.preferredSemester,
      delivery_mode: formData.deliveryMode,
      preferred_schedule: formData.preferredSchedule,
      budget_range: formData.budgetRange || 'Not specified',
      
      // Additional Info
      current_programs: formData.currentAIPrograms || 'None mentioned',
      requirements: formData.specificRequirements || 'None specified',
      
      // Formatted message for email body
      message: `
New AI Pilot Program Request

COLLEGE INFORMATION:
- College Name: ${formData.collegeName}
- Type: ${formData.collegeType}
- Location: ${formData.location}
- Established: ${formData.establishedYear || 'Not specified'}

CONTACT PERSON:
- Name: ${formData.contactPersonName}
- Designation: ${formData.designation}
- Department: ${formData.department}
- Email: ${formData.email}
- Phone: ${formData.countryCode} ${formData.phone}

PROGRAM REQUIREMENTS:
- Target Students: ${formData.targetStudents}
- Expected Batch Size: ${formData.expectedBatchSize}
- Preferred Semester: ${formData.preferredSemester}
- Delivery Mode: ${formData.deliveryMode}
- Preferred Schedule: ${formData.preferredSchedule}
- Budget Range: ${formData.budgetRange || 'Not specified'}

ADDITIONAL INFORMATION:
- Current AI Programs: ${formData.currentAIPrograms || 'None mentioned'}
- Specific Requirements: ${formData.specificRequirements || 'None specified'}

Please contact this college within 24 hours to discuss the pilot program details.
      `.trim()
    };

    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      emailParams
    );

    console.log('Email sent successfully:', response);
    return true;
  } catch (error) {
    console.error('Failed to send email:', error);
    return false;
  }
};

// Fallback email service using mailto (opens user's email client)
export const sendEmailViaMailto = (formData: PilotEnrollmentData): void => {
  const subject = encodeURIComponent(`New AI Pilot Program Request - ${formData.collegeName}`);
  const body = encodeURIComponent(`
New AI Pilot Program Request

COLLEGE INFORMATION:
- College Name: ${formData.collegeName}
- Type: ${formData.collegeType}
- Location: ${formData.location}
- Established: ${formData.establishedYear || 'Not specified'}

CONTACT PERSON:
- Name: ${formData.contactPersonName}
- Designation: ${formData.designation}
- Department: ${formData.department}
- Email: ${formData.email}
- Phone: ${formData.countryCode} ${formData.phone}

PROGRAM REQUIREMENTS:
- Target Students: ${formData.targetStudents}
- Expected Batch Size: ${formData.expectedBatchSize}
- Preferred Semester: ${formData.preferredSemester}
- Delivery Mode: ${formData.deliveryMode}
- Preferred Schedule: ${formData.preferredSchedule}
- Budget Range: ${formData.budgetRange || 'Not specified'}

ADDITIONAL INFORMATION:
- Current AI Programs: ${formData.currentAIPrograms || 'None mentioned'}
- Specific Requirements: ${formData.specificRequirements || 'None specified'}

Please contact this college within 24 hours to discuss the pilot program details.
  `);

  const mailtoLink = `mailto:globalsansys@gmail.com?subject=${subject}&body=${body}`;
  window.open(mailtoLink, '_blank');
};